import React from 'react';

/**
 * Shared mobile layout primitives for operator pillar pages (Phase 2).
 *
 * Pillar pages were built for a wide desktop sidebar layout — `padding: 2rem`,
 * `maxWidth: 1200px`, non-wrapping sub-tab bars, etc. These primitives give
 * each page a consistent, mobile-safe frame so the fixes don't have to be
 * re-invented per page. They render plain containers; the page decides WHAT
 * goes inside.
 */

const PRIMARY = '#1a6b3c';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';
const DARK = '#0f2d1f';

/* ─── MobilePageShell ──────────────────────────────────────────────────────
 * Full-width, light side padding, and clearance for the fixed bottom nav.
 * Optional sticky title header. Use in place of a page's desktop
 * `padding:2rem; maxWidth:1200px` container when on mobile.
 */
export function MobilePageShell({
  title,
  icon,
  right,
  children,
}: {
  title?: string;
  icon?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div style={shellStyles.page}>
      {title && (
        <div style={shellStyles.header}>
          <span style={shellStyles.title}>
            {icon ? `${icon} ` : ''}
            {title}
          </span>
          {right && <span style={shellStyles.headerRight}>{right}</span>}
        </div>
      )}
      <div style={shellStyles.body}>{children}</div>
    </div>
  );
}

/* ─── MobileTabBar ─────────────────────────────────────────────────────────
 * A horizontally SCROLLABLE sub-tab bar. Fixes pages whose desktop tab row has
 * too many tabs to fit a phone (e.g. Seating's 7 tabs). Scroll is silent (no
 * visible scrollbar) with the active tab underlined.
 */
export interface MobileTab {
  key: string;
  label: string;
}

export function MobileTabBar({
  tabs,
  activeKey,
  onSelect,
}: {
  tabs: MobileTab[];
  activeKey: string;
  onSelect: (key: string) => void;
}) {
  return (
    <>
      <style>{`
        .ejt-mtabs::-webkit-scrollbar { display: none; }
        .ejt-mtabs { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
      <nav className="ejt-mtabs" style={tabStyles.bar} aria-label="Section tabs">
        {tabs.map((t) => {
          const active = t.key === activeKey;
          return (
            <button
              key={t.key}
              style={active ? tabStyles.tabActive : tabStyles.tab}
              onClick={() => onSelect(t.key)}
              aria-current={active ? 'page' : undefined}
            >
              {t.label}
            </button>
          );
        })}
      </nav>
    </>
  );
}

/* ─── MobileDesktopOnly ────────────────────────────────────────────────────
 * A friendly placeholder for tools that genuinely need a large screen (e.g.
 * the drag-and-drop floorplan editor). Shown instead of a broken layout.
 */
export function MobileDesktopOnly({
  title,
  message,
}: {
  title: string;
  message?: string;
}) {
  return (
    <div style={shellStyles.desktopOnly}>
      <div style={{ fontSize: '2rem' }}>🖥️</div>
      <div style={shellStyles.desktopOnlyTitle}>{title}</div>
      <div style={shellStyles.desktopOnlyMsg}>
        {message || 'This tool is designed for a larger screen. Open it on a desktop to continue.'}
      </div>
    </div>
  );
}

const shellStyles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100%', backgroundColor: '#f9fafb', paddingBottom: 'calc(72px + env(safe-area-inset-bottom))' },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: '14px 14px 10px',
    position: 'sticky',
    top: 0,
    backgroundColor: '#f9fafb',
    zIndex: 2,
  },
  title: { fontSize: '1.15rem', fontWeight: 800, color: DARK },
  headerRight: { flexShrink: 0 },
  body: { padding: '0 14px' },
  desktopOnly: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 8,
    padding: '36px 24px',
    margin: '12px 0',
    backgroundColor: '#fffbe9',
    border: '1px solid #eadca0',
    borderRadius: 14,
  },
  desktopOnlyTitle: { fontSize: '0.95rem', fontWeight: 700, color: '#8a6d1a' },
  desktopOnlyMsg: { fontSize: '0.82rem', color: '#8a6d1a', lineHeight: 1.5, maxWidth: 280 },
};

const tabStyles: Record<string, React.CSSProperties> = {
  bar: {
    display: 'flex',
    gap: 4,
    overflowX: 'auto',
    overflowY: 'hidden',
    borderBottom: `2px solid ${BORDER}`,
    padding: '0 14px',
    WebkitOverflowScrolling: 'touch',
  },
  tab: {
    flexShrink: 0,
    padding: '10px 14px',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: MUTED,
    background: 'none',
    border: 'none',
    borderBottom: '3px solid transparent',
    marginBottom: '-2px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  tabActive: {
    flexShrink: 0,
    padding: '10px 14px',
    fontSize: '0.9rem',
    fontWeight: 700,
    color: PRIMARY,
    background: 'none',
    border: 'none',
    borderBottom: `3px solid ${PRIMARY}`,
    marginBottom: '-2px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
};
